import { Box, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useLoaderData } from "react-router-dom";
import ProjectWorkflowFoundationApi from "../../../infrastructure/api/projectWorkflowFoundation";
import CustomerRequestTrackingLayout from "./tracking/CustomerRequestTrackingLayout";
import RequestInfoCard from "./RequestInfoCard";
import RequestStep from "./RequestStep";
import RequestSteps from "./RequestSteps";

const Request = ({
  onNavigateToListPage,
  isViewOnly = false,
  source = "Management",
  onOpenContractorModal, // اضافه شد
  onOpenPaymentModal, // اضافه شد
  selectedPackageData, // اضافه شد
  selectedPaymentData, // اضافه شد
}) => {
  const data = useLoaderData("root");
  
  // تابع برای دریافت base URL
  const getBaseUrl = () => {
    if (data?.baseUrl) {
      return data.baseUrl;
    }
    // اگر baseUrl در data نبود، از globalConfig استفاده کن
    return window.globalConfig?.API_URL || "";
  };

  const [steps, setSteps] = useState(
    (data?.stepList || []).map((step) => ({
      ...step,
      collapse: true,
      // step.stepInfoId && step.stepInfoId <= data.request.stepInfoId,
    }))
  );
  const [activeStep, setActiveStep] = useState(0);
  const [shownStep, setShownStep] = useState(0);
  const [workflowV2Summary, setWorkflowV2Summary] = useState(null);

  const [projectTitle, setProjectTitle] = useState(
    data?.request?.projectTitle || ""
  );

  useEffect(() => {
    if (
      data?.stepList &&
      data?.stepList.length > 0 &&
      data?.request?.stepInfoId !== undefined
    ) {
      const isCustomerVisible = (step) =>
        step.showToCustomer === true || step.ShowToCustomer === true;

      // ابتدا پیدا کردن index مرحله فعلی در لیست کامل
      const currentStepIndex = data.stepList.findIndex(
        (x) => x.stepInfoId === data.request.stepInfoId
      );

      if (currentStepIndex === -1) {
        setActiveStep(0);
        setShownStep(0);
        return;
      }

      // فیلتر کردن مراحلی که باید به مشتری نمایش داده شوند
      const customerSteps = data.stepList.filter((step) => isCustomerVisible(step));

      if (customerSteps.length === 0) {
        // اگر هیچ مرحله‌ای برای نمایش به مشتری وجود ندارد، مرحله فعلی را نشان بده
        setActiveStep(currentStepIndex);
        setShownStep(currentStepIndex);
      } else {
        // بررسی اینکه آیا مرحله فعلی خودش قابل نمایش به مشتری است یا خیر
        const currentStepIsCustomerVisible = customerSteps.some(
          (step) => step.stepInfoId === data.request.stepInfoId
        );

        if (currentStepIsCustomerVisible) {
          // اگر مرحله فعلی قابل نمایش است، همان را نشان بده
          setActiveStep(currentStepIndex);
          setShownStep(currentStepIndex);
        } else {
          // اگر مرحله فعلی قابل نمایش نیست، آخرین مرحله قابل نمایش قبل از آن را پیدا کن
          let lastVisibleIndex = -1;

          for (let i = 0; i < data.stepList.length; i++) {
            const step = data.stepList[i];

            // اگر این مرحله قابل نمایش است و قبل از یا مساوی مرحله فعلی است
            if (isCustomerVisible(step) && i <= currentStepIndex) {
              lastVisibleIndex = i;
            }
          }

          if (lastVisibleIndex !== -1) {
            // آخرین مرحله قابل نمایش قبل از مرحله فعلی را نشان بده
            setActiveStep(lastVisibleIndex);
            setShownStep(lastVisibleIndex);
          } else {
            // اگر هیچ مرحله قابل نمایشی قبل از مرحله فعلی نیست، اولین مرحله قابل نمایش را نشان بده
            const firstCustomerStepIndex = data.stepList.findIndex((x) =>
              isCustomerVisible(x)
            );
            setActiveStep(
              firstCustomerStepIndex !== -1 ? firstCustomerStepIndex : 0
            );
            setShownStep(
              firstCustomerStepIndex !== -1 ? firstCustomerStepIndex : 0
            );
          }
        }
      }
    }
  }, [data]);

  useEffect(() => {
    const loadWorkflowV2 = async () => {
      const requestId = data?.request?.id;
      if (!requestId) {
        setWorkflowV2Summary(null);
        return;
      }

      // Only Management / SolarentOperator currently have V2 integration panel
      if (source !== "Management" && source !== "SolarentOperator") {
        setWorkflowV2Summary(null);
        return;
      }

      try {
        const res = await ProjectWorkflowFoundationApi.getWorkflowV2StatusSummary(
          source,
          requestId
        );
        if (res?.ok) setWorkflowV2Summary(res.data || null);
        else setWorkflowV2Summary(null);
      } catch {
        setWorkflowV2Summary(null);
      }
    };

    loadWorkflowV2();
  }, [data?.request?.id, source]);

  const onUpdateActiveStep = (count = 1) => {
    if (activeStep === steps.length - 1) {
      onNavigateToListPage();
    } else {
      const stepIndex = activeStep + count;
      setActiveStep(stepIndex);
      setShownStep(stepIndex);
    }
  };

  if (source === "Customer") {
    return (
      <CustomerRequestTrackingLayout
        data={data}
        steps={steps}
        activeStep={activeStep}
        shownStep={shownStep}
        setShownStep={setShownStep}
        onNavigateToListPage={onNavigateToListPage}
        onUpdateActiveStep={onUpdateActiveStep}
        onOpenContractorModal={onOpenContractorModal}
        onOpenPaymentModal={onOpenPaymentModal}
        selectedPackageData={selectedPackageData}
        selectedPaymentData={selectedPaymentData}
        baseUrl={getBaseUrl()}
        workflowV2Summary={workflowV2Summary}
      />
    );
  }

  return (
    <Grid container spacing={2.5}>
      {/* Request Info Card - Full Width */}
      <Grid item xs={12}>
        <RequestInfoCard
          request={data?.request}
          source={source}
          followUps={data?.followUps || []}
        />
      </Grid>

      {/* Steps and Content Section */}
      <Grid
        item
        xs={12}
        md={3.5}
        lg={3}
        sx={{
          position: { md: "sticky" },
          top: { md: 16 },
          alignSelf: { md: "flex-start" },
        }}
      >
        <RequestSteps
          steps={steps}
          activeStep={activeStep}
          shownStep={shownStep}
          setShownStep={setShownStep}
        />
      </Grid>

      <Grid item xs={12} md={8.5} lg={9}>
        {(steps || []).map((step, index) =>
          index === shownStep ? (
            <Box
              key={index}
              sx={{ display: index === shownStep ? "block" : "none" }}
            >
              <RequestStep
                step={step}
                activeStep={activeStep}
                index={index}
                data={data}
                workflowV2Summary={workflowV2Summary}
                setShownStep={setShownStep}
                isViewOnly={isViewOnly}
                onNavigateToListPage={onNavigateToListPage}
                onUpdateActiveStep={onUpdateActiveStep}
                onOpenContractorModal={onOpenContractorModal}
                onOpenPaymentModal={onOpenPaymentModal}
                selectedPackageData={selectedPackageData}
                selectedPaymentData={selectedPaymentData}
                source={source}
                baseUrl={getBaseUrl()}
              />
            </Box>
          ) : null
        )}
      </Grid>
    </Grid>
  );
};

export default Request;
